import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  MessageCircle, RefreshCw, Loader2, ExternalLink, AtSign, Info,
  Send, CornerDownRight, Zap, Sparkles, PlusCircle
} from 'lucide-react';
import Button from '../ui/Button';
import socialService, { ThreadsReply, SocialReplyPlatform } from '../../services/socialService';

// Badge colors per platform (expand as we add Instagram / Facebook / etc.)
const PLATFORM_STYLES: Record<string, { label: string; className: string }> = {
  threads: { label: 'Threads', className: 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' },
  instagram: { label: 'Instagram', className: 'bg-gradient-to-r from-pink-500 to-orange-400 text-white' },
  facebook: { label: 'Facebook', className: 'bg-blue-600 text-white' },
  youtube: { label: 'YouTube', className: 'bg-red-600 text-white' },
  tiktok: { label: 'TikTok', className: 'bg-black text-white' }
};

interface ThreadsRepliesProps {
  /** Kept for backwards-compat; if false the component shows the empty/connect state. */
  hasThreadsAccount?: boolean;
  /** When true (default), the component shows the multi-platform inbox. */
  hasAnyConnectedSocial?: boolean;
}

const formatRelativeTime = (iso: string, nowFallback: string) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return nowFallback;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return new Date(iso).toLocaleDateString();
};

const REPLY_MAX_LEN = 500;
const SENT_REPLIES_STORAGE_KEY = 'devskin.socialReplies.sent';

const loadSentReplies = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(SENT_REPLIES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const ThreadsReplies: React.FC<ThreadsRepliesProps> = ({
  hasThreadsAccount = true,
  hasAnyConnectedSocial
}) => {
  // The component used to require a connected Threads account specifically.
  // It now spans every Meta-owned platform we support, so the gate is "any
  // supported social account is connected" — falling back to the old prop
  // when the new one is not provided keeps existing call sites working.
  const isConnected = hasAnyConnectedSocial ?? hasThreadsAccount;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [replies, setReplies] = useState<ThreadsReply[]>([]);
  const [accountUsername, setAccountUsername] = useState<string | null>(null);
  const [postsScanned, setPostsScanned] = useState(0);
  const [failedPosts, setFailedPosts] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  // Per-reply pending state — sending one reply must NOT block the others.
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  // Persist replies the user has already sent so refresh / re-render keeps the
  // green confirmation visible and prevents an accidental duplicate send.
  const [sentReplies, setSentReplies] = useState<Record<string, string>>(loadSentReplies);

  useEffect(() => {
    try {
      localStorage.setItem(SENT_REPLIES_STORAGE_KEY, JSON.stringify(sentReplies));
    } catch {
      // localStorage may be disabled (private mode) — silently degrade.
    }
  }, [sentReplies]);

  const fetchReplies = useCallback(async (silent = false) => {
    if (silent) setIsRefreshing(true);
    else setIsLoading(true);
    try {
      const res = await socialService.getSocialReplies();
      if (res.success) {
        setReplies(res.data || []);
        setPostsScanned(res.posts_count || 0);
        setFailedPosts(res.failed_posts || 0);
        setAccountUsername(res.account?.username || null);
      } else {
        toast.error(res.message || t('social.socialReplies.loadError', 'Could not load replies'));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('social.socialReplies.loadError', 'Could not load replies');
      toast.error(msg);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    if (isConnected) fetchReplies();
    else setIsLoading(false);
  }, [isConnected, fetchReplies]);

  const handleOpenReply = (commentId: string) => {
    setReplyingTo(commentId);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const markSending = (id: string, on: boolean) =>
    setSendingIds(prev => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  const handleSendReply = async (commentId: string, platform: SocialReplyPlatform) => {
    const msg = replyText.trim();
    if (!msg) {
      toast.error(t('social.socialReplies.emptyMessage', 'Write a message before sending'));
      return;
    }
    if (msg.length > REPLY_MAX_LEN) {
      toast.error(t('social.socialReplies.tooLong', 'Reply exceeds {{n}} characters', { n: REPLY_MAX_LEN }));
      return;
    }
    if (sentReplies[commentId]) {
      // Defensive — UI hides the button when this is true, but if it fires
      // through any path (keyboard shortcut, double-click race) we abort.
      return;
    }
    markSending(commentId, true);
    try {
      const res = await socialService.replyToSocialComment(platform, commentId, msg);
      if (res.success) {
        toast.success(t('social.socialReplies.sent', 'Reply sent'));
        setSentReplies(prev => ({ ...prev, [commentId]: msg }));
        setReplyingTo(null);
        setReplyText('');
      } else {
        toast.error((res as any).message || t('social.socialReplies.sendError', 'Could not send the reply'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('social.socialReplies.sendError', 'Could not send the reply'));
    } finally {
      markSending(commentId, false);
    }
  };

  if (!isConnected) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <AtSign className="w-10 h-10 mx-auto text-gray-400 mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {t('social.socialReplies.notConnectedTitle', 'Connect a social account')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('social.socialReplies.notConnectedDesc', 'Connect a supported social account (e.g. Threads) in the Accounts tab to see replies to your posts here.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            {t('social.socialReplies.title', 'Social Replies')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t(
              'social.socialReplies.subtitle',
              'Replies to your posts across connected Meta platforms (Threads, Instagram, Facebook).'
            )}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => fetchReplies(true)}
          disabled={isRefreshing || isLoading}
        >
          {isRefreshing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          <span className="ml-2">{t('social.socialReplies.refresh', 'Refresh')}</span>
        </Button>
      </div>

      {/* Automation CTA — guides the user toward setting up auto-replies */}
      <div className="p-4 rounded-lg border border-purple-200 dark:border-purple-800 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-purple-600 flex items-center justify-center">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t('social.socialReplies.automationTitle', 'Automate your replies')}
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
            {t(
              'social.socialReplies.automationDesc',
              'Create a workflow to reply automatically whenever someone comments on your posts. It works across all connected social platforms.'
            )}
          </p>
        </div>
        <button
          onClick={() => navigate('/workflows/new')}
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white"
        >
          <PlusCircle className="w-3.5 h-3.5" />
          {t('social.socialReplies.createWorkflow', 'Create workflow')}
        </button>
      </div>

      {/* Permission note — kept for transparency (and helpful to Meta reviewers) */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <p className="text-xs text-blue-700 dark:text-blue-300">
          {t(
            'social.socialReplies.scopeNote',
            'Showing public comments on the 10 most recent posts you published from this app on each connected platform. Direct messages and comments on older posts are not shown here.'
          )}
        </p>
      </div>

      {/* Scope summary */}
      {accountUsername && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {t('social.socialReplies.scannedLabel', 'Scanned {{count}} recent posts from @{{username}}', {
            count: postsScanned,
            username: accountUsername
          })}
          {failedPosts > 0 && (
            <span className="ml-2 text-amber-600 dark:text-amber-400">
              · {t('social.socialReplies.partialFailure', '{{n}} post(s) could not be loaded', { n: failedPosts })}
            </span>
          )}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && replies.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
          <MessageCircle className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            {t('social.socialReplies.emptyTitle', 'No replies yet')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {t(
              'social.socialReplies.emptyDesc',
              'Once people comment on your posts, their replies will show up here so you can respond individually or automate answers.'
            )}
          </p>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              onClick={() => navigate('/workflows/new')}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Sparkles className="w-4 h-4" />
              {t('social.socialReplies.setupAutoReply', 'Set up auto-reply')}
            </button>
          </div>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-3 max-w-sm mx-auto">
            {t(
              'social.socialReplies.emptyHint',
              'Tip: Workflows can filter by keyword and answer with AI using your brand context.'
            )}
          </p>
        </div>
      )}

      {/* Replies list */}
      {!isLoading && replies.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
          {replies.map((reply) => {
            const platformKey = (reply.platform || 'threads') as SocialReplyPlatform;
            const platformStyle = PLATFORM_STYLES[platformKey] || PLATFORM_STYLES.threads;
            const isSending = sendingIds.has(reply.id);
            const remaining = REPLY_MAX_LEN - replyText.length;
            const postUrl = reply.post_url || reply.threads_post_url || null;
            return (
            // Composite key: reply IDs MAY repeat across posts in pathological API responses.
            <div key={`${reply.post_id}-${reply.id}`} className="p-4 flex gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {(reply.username || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">
                    @{reply.username || 'user'}
                  </span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${platformStyle.className}`}>
                    {platformStyle.label}
                  </span>
                  <span className="text-xs text-gray-400">
                    {formatRelativeTime(reply.timestamp, t('social.socialReplies.now', 'now'))}
                  </span>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200 mt-0.5 whitespace-pre-wrap break-words">
                  {reply.text || <em className="text-gray-400">{t('social.socialReplies.noText', '(no text)')}</em>}
                </p>
                {reply.post_preview && (
                  <a
                    href={postUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => { if (!postUrl) e.preventDefault(); }}
                    className="mt-2 block bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 hover:border-primary-400 transition-colors"
                  >
                    <div className="flex items-center gap-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      <CornerDownRight className="w-3 h-3" />
                      {t('social.socialReplies.replyingTo', 'In reply to your post')}
                    </div>
                    <p className="text-xs text-gray-700 dark:text-gray-300 mt-0.5 line-clamp-2">
                      {reply.post_preview}
                    </p>
                  </a>
                )}
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                  {postUrl && (
                    <a
                      href={postUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {t('social.socialReplies.viewOnPlatform', 'View on {{platform}}', { platform: platformStyle.label })}
                    </a>
                  )}
                  {!sentReplies[reply.id] && replyingTo !== reply.id && (
                    <button
                      onClick={() => handleOpenReply(reply.id)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400"
                    >
                      <CornerDownRight className="w-3 h-3" />
                      {t('social.socialReplies.replyAction', 'Reply')}
                    </button>
                  )}
                </div>

                {replyingTo === reply.id && !sentReplies[reply.id] && (
                  <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/40">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={2}
                      maxLength={REPLY_MAX_LEN}
                      placeholder={t('social.socialReplies.replyPlaceholder', 'Type your reply…')}
                      className="w-full text-sm bg-transparent border-0 focus:ring-0 focus:outline-none resize-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    />
                    <div className="flex items-center justify-between mt-1 gap-2">
                      <span className={`text-[10px] ${remaining < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                        {remaining}/{REPLY_MAX_LEN} ·{' '}
                        {t('social.socialReplies.replyingViaPlatform', 'Replying on {{platform}}', { platform: platformStyle.label })}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancelReply}
                          className="text-xs px-3 py-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                        >
                          {t('social.socialReplies.cancel', 'Cancel')}
                        </button>
                        <button
                          onClick={() => handleSendReply(reply.id, platformKey)}
                          disabled={isSending || !replyText.trim() || replyText.length > REPLY_MAX_LEN}
                          className="text-xs px-3 py-1 rounded-md bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          {isSending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Send className="w-3 h-3" />
                          )}
                          {t('social.socialReplies.send', 'Send reply')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {sentReplies[reply.id] && (
                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-xs text-green-700 dark:text-green-300">
                    <span className="font-medium">
                      {t('social.socialReplies.sentLabel', 'Your reply:')}
                    </span>{' '}
                    {sentReplies[reply.id]}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThreadsReplies;
